/**
 * UnifiedTagModal View Component
 *
 * A unified interface that seamlessly combines tag selection and management.
 * Users can select tags for players and manage the tag library in one place,
 * with mode switching for an intuitive experience.
 * Uses a plain VStack instead of ModalBody (which is a ScrollView) to avoid
 * nested ScrollViews that cause touch event interception on large tag lists.
 *
 * ## Chrome (SE-6554 restyle)
 *
 * The dialog's surfaces used to be painted from the `KanbanColors` palette
 * passed down as a prop (`mainBackground`, `columnHeader`, `border`), plus an
 * off-ladder 16px radius. They are semantic tokens now, so the modal flips
 * with the theme at the config level rather than through a second palette:
 *
 *   - `surface-base`   → background-0   255,255,255 light / 18,18,18 dark
 *   - `surface-raised` → background-50  246,246,246 light / 39,38,37 dark
 *   - `content-primary`→ typography-900  38,38,39  light / 245,245,245 dark
 *   - `outline-subtle` → outline-100    230,230,230 light / 65,65,65 dark
 *
 * The kanban palette is no longer threaded into this view at all: the manage
 * rows now colour their own glyphs from status/content tokens, so the last
 * consumer of it is gone.
 * @module features/player-kanban/components/UnifiedTagModal
 */

import { Check, Plus, Search, X } from "lucide-react-native";
import { memo, type ReactNode } from "react";

import { type Tag } from "@/features/player-kanban/types/types";
import ColorPickerButton from "@/features/player-kanban/components/molecules/ColorPickerButton";
import TagManageRow from "@/features/player-kanban/components/molecules/TagManageRow";
import TagSelectRow from "@/features/player-kanban/components/molecules/TagSelectRow";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";

/**
 * Background-color style for a data-driven tag/overlay swatch.
 * design-system: dynamic-style waiver — data-driven value (tag/theme color)
 * @param color - Resolved color string
 */
const bgColorStyle = (color: string) => ({ backgroundColor: color });

/**
 * Renders tag in edit mode with input field and save/cancel buttons
 * @param tag - The tag being edited
 * @param editingName - Current value of the tag name input
 * @param onEditNameChange - Callback when tag name input changes
 * @param onSaveEdit - Callback to save tag name changes
 * @param onCancelEdit - Callback to cancel editing
 */
const renderEditMode = (
  tag: Tag & { color: string },
  editingName: string,
  onEditNameChange: (value: string) => void,
  onSaveEdit: () => void,
  onCancelEdit: () => void
): ReactNode => (
  <Stack direction="horizontal" className="items-center gap-2 p-3">
    {/* design-system: dynamic-style waiver — data-driven value (tag color) */}
    <Box
      className="size-4 rounded-full"
      // eslint-disable-next-line no-restricted-syntax -- data-driven tag.color swatch; no closed color token
      UNSAFE_style={bgColorStyle(tag.color)}
    />
    <Input
      variant="outline"
      size="md"
      className="h-9 flex-1 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
      field={{
        value: editingName,
        onChangeText: onEditNameChange,
        "aria-label": "Tag name",
      }}
    />
    <Pressable
      onPress={onSaveEdit}
      className="rounded-none bg-status-success/10 p-1"
      accessible
      accessibilityLabel="Save tag name"
      accessibilityRole="button"
    >
      <Icon as={Check} size="md" className="text-status-success" />
    </Pressable>
    <Pressable
      onPress={onCancelEdit}
      className="rounded-none bg-status-error/10 p-1"
      accessible
      accessibilityLabel="Cancel editing"
      accessibilityRole="button"
    >
      <Icon as={X} size="md" className="text-status-error" />
    </Pressable>
  </Stack>
);

/**
 * Renders create tag button with optional color picker
 * @param canCreateFromSearch - Whether a new tag can be created from search
 * @param searchQuery - Current search query text
 * @param isCreating - Whether tag creation is in progress
 * @param showColorPicker - Whether to show color picker
 * @param selectedColor - Currently selected color
 * @param availableColors - Array of color options
 * @param onCreateFromSearch - Callback to create tag from search
 * @param onToggleColorPicker - Callback to toggle color picker
 * @param onColorSelect - Callback when color is selected
 */
const renderCreateTagSection = (
  canCreateFromSearch: boolean,
  searchQuery: string,
  isCreating: boolean,
  showColorPicker: boolean,
  selectedColor: string,
  availableColors: string[],
  onCreateFromSearch: () => void,
  onToggleColorPicker: () => void,
  onColorSelect: (color: string) => void
): React.ReactElement => {
  if (!canCreateFromSearch || !searchQuery.trim()) return <></>;

  return (
    <Stack className="gap-3 pt-3">
      <Button
        label={`Create "${searchQuery.trim()}"`}
        variant="solid"
        tone="accent"
        size="sm"
        icon={Plus}
        iconPosition="leading"
        isDisabled={isCreating}
        onPress={onCreateFromSearch}
        accessibilityLabel={`Create tag ${searchQuery.trim()}`}
        className="self-start"
      />

      <Stack className="gap-2">
        <Pressable
          onPress={onToggleColorPicker}
          className="flex-row items-center gap-2"
        >
          <Text variant="caption" className="text-content-muted">
            Select color
          </Text>
          {/* design-system: dynamic-style waiver — data-driven value (selected tag color) */}
          <Box
            className="size-6 rounded-full border-2 border-outline-subtle"
            // eslint-disable-next-line no-restricted-syntax -- data-driven selectedColor swatch; no closed color token
            UNSAFE_style={bgColorStyle(selectedColor)}
          />
        </Pressable>
        {showColorPicker && availableColors && (
          <Stack direction="horizontal" className="flex-wrap gap-2">
            {availableColors.map(color => (
              <ColorPickerButton
                key={color}
                color={color}
                selectedColor={selectedColor}
                onSelect={onColorSelect}
                accessibilityPrefix="Select"
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * Renders color picker section for tag color editing
 * @param editingColorTagId - ID of tag whose color is being edited, or null if none
 * @param tag - The tag to check against editingColorTagId
 * @param availableColors - Array of color options to display
 * @param selectedColor - Currently selected color
 * @param onSaveColorChange - Callback to save color change
 * @param onCancelColorEdit - Callback to cancel color editing
 */
const renderColorPicker = (
  editingColorTagId: string | null,
  tag: Tag & { color: string },
  availableColors: string[],
  selectedColor: string,
  onSaveColorChange: (color: string) => void,
  onCancelColorEdit: () => void
): React.ReactElement => {
  if (editingColorTagId !== tag.id) return <></>;

  return (
    <Box className="border-t border-t-outline-subtle bg-surface-raised p-3">
      <Stack className="gap-3">
        <Text variant="caption" className="font-medium text-content-muted">
          SELECT NEW COLOR
        </Text>
        <Stack direction="horizontal" className="flex-wrap gap-2">
          {availableColors.map(color => (
            <ColorPickerButton
              key={color}
              color={color}
              selectedColor={selectedColor}
              onSelect={onSaveColorChange}
              accessibilityPrefix="Change tag color to"
            />
          ))}
        </Stack>
        <Pressable
          onPress={onCancelColorEdit}
          className="self-start rounded-none bg-status-error/10 px-3 py-1"
          accessible
          accessibilityLabel="Cancel color change"
          accessibilityRole="button"
        >
          <Text
            variant="unstyled"
            className="font-medium text-caption text-status-error"
          >
            Cancel
          </Text>
        </Pressable>
      </Stack>
    </Box>
  );
};

/**
 * Props for UnifiedTagModal view
 */
interface UnifiedTagModalViewProps {
  isOpen: boolean;
  mode: "select" | "manage";
  searchQuery: string;
  filteredTags: (Tag & { color: string })[];
  selectedTagIds: string[];
  editingTagId: string | null;
  editingName: string;
  isCreating: boolean;
  canCreateFromSearch: boolean;
  tagUsageCounts: Record<string, number>;
  showApplyButton: boolean;
  showColorPicker: boolean;
  selectedColor: string;
  availableColors: string[];
  editingColorTagId: string | null;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onTagToggle: (tagId: string) => void;
  onCreateFromSearch: () => void;
  onQuickCreate: (name: string) => void;
  onStartEdit: (tagId: string, currentName: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditNameChange: (value: string) => void;
  onDeleteTag: (tagId: string) => void;
  onColorSelect: (color: string) => void;
  onToggleColorPicker: () => void;
  onStartColorEdit: (tagId: string, currentColor: string) => void;
  onSaveColorChange: (color: string) => void;
  onCancelColorEdit: () => void;
  onApply: () => void;
  onModeSwitch: () => void;
}

/**
 * UnifiedTagModal View - Unified tag interface for selection and management
 * @param props - View props
 * @param props.isOpen - Whether the modal is currently visible
 * @param props.mode - Current modal mode: 'select' for tag selection or 'manage' for tag management
 * @param props.searchQuery - Current value in the tag search input field
 * @param props.filteredTags - Tags matching the current search query
 * @param props.selectedTagIds - Array of tag IDs currently selected for the player
 * @param props.editingTagId - ID of tag currently being edited, or null if none
 * @param props.editingName - Current value in the tag name edit field
 * @param props.isCreating - Whether a new tag is currently being created
 * @param props.canCreateFromSearch - Whether the search query can be used to create a new tag
 * @param props.tagUsageCounts - Number of times each tag is used across players
 * @param props.showApplyButton - Whether to show the Apply button in select mode
 * @param props.onClose - Callback fired when user closes the modal
 * @param props.onSearchChange - Callback fired when search input value changes
 * @param props.onTagToggle - Callback fired when user toggles tag selection
 * @param props.onCreateFromSearch - Callback fired when user creates new tag from search query
 * @param props.onQuickCreate - Callback fired when user quickly creates a tag with the given name
 * @param props.onStartEdit - Callback fired when user starts editing a tag with its current name
 * @param props.onSaveEdit - Callback fired when user saves tag name edit
 * @param props.onCancelEdit - Callback fired when user cancels tag name edit
 * @param props.onEditNameChange - Callback fired when tag name edit input changes
 * @param props.onDeleteTag - Callback fired when user deletes a tag
 * @param props.onApply - Callback fired when user applies selected tags in select mode
 * @param props.onModeSwitch - Callback fired when user switches between select and manage modes
 * @param props.showColorPicker - Whether the color picker is currently visible
 * @param props.selectedColor - The color currently selected in the color picker
 * @param props.availableColors - Array of color hex values available for tag colors
 * @param props.onColorSelect - Callback fired when user selects a color from the picker
 * @param props.onToggleColorPicker - Callback fired when user toggles color picker visibility
 * @param props.editingColorTagId - ID of tag whose color is currently being edited, or null
 * @param props.onStartColorEdit - Callback fired when user starts editing tag color with current color
 * @param props.onSaveColorChange - Callback fired when user saves tag color change
 * @param props.onCancelColorEdit - Callback fired when user cancels tag color edit
 */
const UnifiedTagModalView = ({
  isOpen,
  mode,
  searchQuery,
  filteredTags,
  selectedTagIds,
  editingTagId,
  editingName,
  isCreating,
  canCreateFromSearch,
  tagUsageCounts,
  showApplyButton,
  showColorPicker,
  selectedColor,
  availableColors,
  editingColorTagId,
  onClose,
  onSearchChange,
  onTagToggle,
  onCreateFromSearch,
  onQuickCreate: _onQuickCreate,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditNameChange,
  onDeleteTag,
  onColorSelect,
  onToggleColorPicker,
  onStartColorEdit,
  onSaveColorChange,
  onCancelColorEdit,
  onApply,
  onModeSwitch: _onModeSwitch,
}: UnifiedTagModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="md">
    <ModalBackdrop />
    {/* The dialog was capped at a computed 480px. `max-h-96` (384) is the
        tallest rung on the closed max-height scale and the same bound
        `NewPipelineModalView` uses, so the cap is a token rather than an
        arbitrary value — 96px shorter than before. */}
    <ModalContent testID="unified-tag-modal" className="max-h-96 !p-0">
      <ModalHeader className="border-b border-b-outline-subtle bg-surface-raised px-5 py-4">
        <Stack
          direction="horizontal"
          className="w-full items-center justify-between"
        >
          {/* No decorative glyph beside the title — the modal family leads with
              the title alone. */}
          <Text variant="title-lg" className="text-content-primary">
            {mode === "select" ? "Select tags" : "Manage tags"}
          </Text>
          {/* Close Button */}
          <Pressable
            onPress={onClose}
            className="p-1"
            accessible
            accessibilityLabel="Close tag modal"
            accessibilityRole="button"
          >
            <Icon as={X} size="sm" className="text-content-muted" />
          </Pressable>
        </Stack>
      </ModalHeader>

      <Stack className="flex-1 bg-surface-base">
        {/* Search Bar with Create Option */}
        <Box className="border-b border-b-outline-subtle bg-surface-base px-5 py-4">
          <Input
            variant="outline"
            size="md"
            className="h-9 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
            leadingIcon={Search}
            leadingIconTone="muted"
            field={{
              testID: "unified-tag-modal-search-input",
              placeholder: "Search or create a tag",
              value: searchQuery,
              onChangeText: onSearchChange,
              "aria-label": "Search or create a tag",
            }}
          />

          {renderCreateTagSection(
            canCreateFromSearch,
            searchQuery,
            isCreating,
            showColorPicker,
            selectedColor,
            availableColors,
            onCreateFromSearch,
            onToggleColorPicker,
            onColorSelect
          )}
        </Box>

        {/* Tag List */}
        <ScrollView
          className="flex-1 bg-surface-base"
          showsVerticalScrollIndicator={false}
        >
          <Stack className="gap-2 p-5">
            {!filteredTags || filteredTags.length === 0 ? (
              <Box className="py-8">
                <Text variant="body" className="text-center text-content-muted">
                  {searchQuery
                    ? `No tags found for "${searchQuery}"`
                    : "No tags created yet"}
                </Text>
              </Box>
            ) : (
              filteredTags.map(tag => (
                <Box
                  key={tag.id}
                  className={`rounded-sm border bg-surface-raised ${
                    selectedTagIds.includes(tag.id)
                      ? "border-accent-primary"
                      : "border-outline-subtle"
                  }`}
                >
                  {editingTagId === tag.id ? (
                    renderEditMode(
                      tag,
                      editingName,
                      onEditNameChange,
                      onSaveEdit,
                      onCancelEdit
                    )
                  ) : mode === "select" ? (
                    <TagSelectRow
                      tag={tag}
                      selectedTagIds={selectedTagIds}
                      tagUsageCounts={tagUsageCounts}
                      onTagToggle={onTagToggle}
                    />
                  ) : (
                    <TagManageRow
                      tag={tag}
                      tagUsageCounts={tagUsageCounts}
                      onStartColorEdit={onStartColorEdit}
                      onStartEdit={onStartEdit}
                      onDeleteTag={onDeleteTag}
                    />
                  )}

                  {renderColorPicker(
                    editingColorTagId,
                    tag,
                    availableColors,
                    selectedColor,
                    onSaveColorChange,
                    onCancelColorEdit
                  )}
                </Box>
              ))
            )}
          </Stack>
        </ScrollView>
      </Stack>

      {/* Footer with Apply Button (only in select mode for players) */}
      {mode === "select" && showApplyButton && (
        <ModalFooter className="border-t border-t-outline-subtle bg-surface-raised px-5 py-4">
          <Stack
            direction="horizontal"
            className="w-full items-center justify-end gap-3"
          >
            <Button
              onPress={onClose}
              label="Cancel"
              variant="outline"
              tone="secondary"
              size="md"
              accessibilityLabel="Cancel"
            />
            {/* `accent`, not `primary`: Gluestack's solid `primary` action is
                near-black here, so the confirm button rendered dark instead of
                the product's blue CTA (SE-6233). */}
            <Button
              testID="unified-tag-modal-apply"
              onPress={onApply}
              label={`Apply tags (${selectedTagIds.length})`}
              variant="solid"
              tone="accent"
              size="md"
              accessibilityLabel="Apply tags"
            />
          </Stack>
        </ModalFooter>
      )}
    </ModalContent>
  </Modal>
);

UnifiedTagModalView.displayName = "UnifiedTagModalView";

export default memo(UnifiedTagModalView);
